import { api } from './api'
import { throwApiError } from './apiHelpers'

function assertSuccess(body) {
  if (!body?.success) {
    const err = new Error(body?.message || 'Request failed')
    err.code = body?.error?.code
    throw err
  }
  return body.data
}

export const organizationsApi = {
  async list(params: { q?: string; status?: string; missingPhone?: boolean; page?: number; limit?: number } = {}) {
    try {
      const search = new URLSearchParams()
      const q = params.q?.trim()
      if (q) search.set('q', q)
      if (params.status?.trim()) search.set('status', params.status.trim())
      if (params.missingPhone) search.set('missingPhone', 'true')
      if (params.page != null) search.set('page', String(params.page))
      if (params.limit != null) search.set('limit', String(params.limit))
      const qs = search.toString()
      const { data } = await api.get(`/admin/organisations${qs ? `?${qs}` : ''}`)
      const payload = assertSuccess(data)
      if (Array.isArray(payload)) {
        return {
          items: payload,
          total: payload.length,
          page: 1,
          limit: payload.length,
          summary: {
            total: payload.length,
            active: payload.filter((o) => o.status === 'active').length,
            inactive: payload.filter((o) => o.status === 'inactive').length,
            suspended: payload.filter((o) => o.status === 'suspended').length,
          },
        }
      }
      return payload
    } catch (e) {
      throwApiError(e, 'Failed to load organizations')
    }
  },

  async getById(id) {
    try {
      const { data } = await api.get(`/admin/organisations/${encodeURIComponent(id)}`)
      return assertSuccess(data)
    } catch (e) {
      throwApiError(e, 'Failed to load organization')
    }
  },

  async listStores(organisationId: string | number) {
    try {
      const { data } = await api.get(
        `/admin/organisations/${encodeURIComponent(String(organisationId))}/stores`,
      )
      const payload = assertSuccess(data)
      return payload?.items != null ? payload : { items: [] }
    } catch (e) {
      throwApiError(e, 'Failed to load stores')
    }
  },

  async create(payload) {
    try {
      const { data } = await api.post('/admin/organisations', payload)
      return assertSuccess(data)
    } catch (e) {
      throwApiError(e, 'Failed to create organization')
    }
  },

  async update(id, payload) {
    try {
      const { data } = await api.patch(`/admin/organisations/${encodeURIComponent(id)}`, payload)
      return assertSuccess(data)
    } catch (e) {
      throwApiError(e, 'Failed to update organization')
    }
  },

  async delete(id) {
    try {
      const { data } = await api.delete(`/admin/organisations/${encodeURIComponent(id)}`)
      return assertSuccess(data)
    } catch (e) {
      throwApiError(e, 'Failed to delete organization')
    }
  },

  async resendOwnerLoginDetails(id) {
    try {
      const { data } = await api.post(
        `/admin/organisations/${encodeURIComponent(id)}/resend-owner-login`,
      )
      return assertSuccess(data)
    } catch (e) {
      throwApiError(e, 'Failed to resend owner login details')
    }
  },

  async sendStorefrontWelcome(id) {
    try {
      const { data } = await api.post(
        `/admin/organisations/${encodeURIComponent(id)}/send-storefront-welcome`,
      )
      return assertSuccess(data)
    } catch (e) {
      throwApiError(e, 'Failed to send storefront welcome email')
    }
  },

  async uploadLogo(file, onProgress) {
    try {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post('/admin/uploads/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (evt) => {
          if (!onProgress) return
          const total = evt.total || file.size || 0
          if (!total) return
          const percent = Math.min(100, Math.round((evt.loaded / total) * 100))
          onProgress(percent)
        },
      })
      const payload = assertSuccess(data)
      // API returns `{ items: [{ url, publicId, ... }] }` (same as merchant multi-upload).
      const item = payload?.items?.[0]
      if (!item?.url) {
        const err = new Error('Upload succeeded but no image URL was returned.')
        err.code = 'UPLOAD_RESPONSE_INVALID'
        throw err
      }
      return item
    } catch (e) {
      throwApiError(e, 'Failed to upload image')
    }
  },
}
